import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, TrendingDown, Eye } from "lucide-react";
import { getAssignmentDetail } from "@/lib/api";
import { DEPTHS } from "./constants";
import { Stat, StatusBadge } from "./helpers";

// Assignment detail — submissions matrix + class-wide weak concepts.
export const AssignmentDetail = ({ classroom, assignment, onClose, onPreviewLecture }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    getAssignmentDetail(classroom.id, assignment.id).then(setData).catch(() => setData(null));
  }, [classroom.id, assignment.id]);
  if (!data) return <div className="grid place-items-center py-12"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;

  const canPreview = data.mode === "shared" && data.shared_lecture_id && onPreviewLecture;

  return (
    <div>
      <button onClick={onClose} className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition">
        <ArrowLeft className="h-4 w-4" /> Back to class
      </button>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-serif text-2xl font-bold">{data.title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {data.mode === "shared" ? "Shared lecture" : `Each student generates: ${data.book_title}${data.chapters ? ` · ${data.chapters}` : ""}${data.edition ? ` · ${data.edition}` : ""}`}
            {" · "}{DEPTHS.find((d) => d.id === data.depth)?.label || data.depth}
          </p>
          <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
            {data.min_score > 0 && <span>Pass ≥ {Math.round(data.min_score)}%</span>}
            {data.min_minutes > 0 && <span>· {data.min_minutes} min on lecture</span>}
          </div>
        </div>
        {canPreview && (
          <button
            data-testid="preview-assigned-lecture-btn"
            onClick={() => onPreviewLecture(data.shared_lecture_id)}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:opacity-90 transition shrink-0"
          >
            <Eye className="h-4 w-4" /> Preview lecture
          </button>
        )}
      </div>

      {data.submissions.every((s) => s.status === "not_started") && (
        <div className="mt-6 rounded-2xl border border-dashed border-accent/40 bg-accent/5 px-5 py-4 text-sm" data-testid="asg-empty-hint">
          <p className="font-semibold">
            {data.submissions.length === 0
              ? "No students in this class yet."
              : "No student has started this assignment yet."}
          </p>
          <p className="mt-1 text-muted-foreground">
            {data.mode === "shared"
              ? <>Share your class code <span className="font-mono font-semibold text-foreground">{classroom.code}</span> or bulk-invite students to have them see this assignment. Meanwhile, click <span className="font-semibold">Preview lecture</span> above to see exactly what they'll get.</>
              : <>Share your class code <span className="font-mono font-semibold text-foreground">{classroom.code}</span> or bulk-invite students. When a student starts this assignment, Lectura generates a fresh lecture for them from <span className="font-semibold">{data.book_title}{data.chapters ? ` · ${data.chapters}` : ""}</span>.</>
            }
          </p>
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label="Class average" value={data.class_average != null ? `${data.class_average}%` : "—"} tone="accent" />
        <Stat label="Completed" value={`${data.submissions.filter((s) => s.status === "completed").length} / ${data.submissions.length}`} />
        <Stat label="Failed / stuck" value={`${data.submissions.filter((s) => s.status === "failed").length}`} />
      </div>

      <h4 className="font-serif text-lg font-bold mt-8 mb-3">Submissions</h4>
      <div className="rounded-2xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-[0.15em] text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold">Student</th>
              <th className="text-left px-4 py-2.5 font-semibold">Status</th>
              <th className="text-right px-4 py-2.5 font-semibold">Score</th>
              <th className="text-right px-4 py-2.5 font-semibold">Time</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.submissions.map((r) => (
              <tr key={r.student_id} data-testid={`sub-${r.student_id}`}>
                <td className="px-4 py-2.5">
                  <div className="font-semibold">{r.name || r.email.split("@")[0]}</div>
                  <div className="text-xs text-muted-foreground">{r.email}</div>
                </td>
                <td className="px-4 py-2.5">
                  <StatusBadge status={r.status} />
                </td>
                <td className="px-4 py-2.5 text-right font-mono">
                  {r.score != null ? `${Math.round(r.score)}%` : "—"}
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-muted-foreground">
                  {r.minutes || 0}m
                </td>
              </tr>
            ))}
            {data.submissions.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-muted-foreground">No students enrolled yet — share your join code.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data.weak_concepts.length > 0 && (
        <>
          <h4 className="font-serif text-lg font-bold mt-8 mb-3 flex items-center gap-2"><TrendingDown className="h-4 w-4 text-accent" /> Class-wide weak spots</h4>
          <div className="rounded-2xl border border-border p-4 space-y-2">
            {data.weak_concepts.map((w) => (
              <div key={w.concept} className="flex items-center gap-3">
                <span className="flex-1 text-sm font-semibold truncate">{w.concept}</span>
                <div className="flex-1 max-w-[240px] h-2 rounded-full bg-border overflow-hidden">
                  <div className="h-full bg-accent" style={{ width: `${w.pct}%` }} />
                </div>
                <span className="text-xs font-mono text-muted-foreground w-16 text-right">{w.pct}% · {w.students} kids</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
